/**
 * Which held recordings no longer have a note to belong to. Pure and Angular-free
 * so the rule can be checked directly.
 *
 * A recording is only reachable through its note: the note is what shows it is
 * waiting, and what the words are written back to once it is read. A recording
 * whose note has gone is therefore unreachable rather than merely untidy — no
 * screen can show it and nothing will ever read it — while still holding a
 * recording of a conversation on the device.
 */

/**
 * The held recordings with no note behind them.
 *
 * `noteIds` must be every note on the device, read in a way that tells an empty
 * device apart from a failed read. Passing the results of a read that answers
 * failure with an empty list would make every recording look orphaned.
 */
export function orphanedRecordings(
  held: readonly string[] | null | undefined,
  noteIds: Iterable<string> | null | undefined,
): string[] {
  const kept = new Set(noteIds ?? []);
  return (held ?? []).filter((noteId) => !kept.has(noteId));
}

/**
 * Let go of every held recording whose note has gone, and answer with the ones
 * still worth reading.
 *
 * `readNoteIds` must raise rather than answer with nothing when it cannot read:
 * an empty answer and a failed read look alike from here, and deleting on the
 * second would take every recording on the device. A failure therefore keeps
 * everything and leaves the work for the next sweep.
 */
export async function discardOrphans(
  held: readonly string[] | null | undefined,
  readNoteIds: () => Promise<readonly string[]>,
  remove: (noteId: string) => Promise<void>,
): Promise<string[]> {
  const waiting = [...(held ?? [])];
  if (!waiting.length) {
    return waiting;
  }
  let orphans: string[];
  try {
    orphans = orphanedRecordings(waiting, await readNoteIds());
  } catch (e) {
    console.warn('Could not read the notes; keeping every recording', e);
    return waiting;
  }
  for (const noteId of orphans) {
    await remove(noteId);
  }
  return waiting.filter((noteId) => !orphans.includes(noteId));
}
